/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { notification, UploadFile } from "@duxton-web/ui"
import dayjs from "dayjs"

import {
  DECISION_ACTION,
  DECISION_ACTION_TO_RESOLUTION,
  DECISION_ACTION_TO_STATUS,
  DecisionAction,
  INSPECTION_OUTCOME,
  InspectionDecision,
  LEGACY_WORKLIST_STATUS_TO_DETAIL_STATUS,
  RETURN_CONTEXTUAL_ACTION,
  RETURN_MEDIA_MAX_PHOTO_SIZE_BYTES,
  RETURN_MEDIA_SOURCE,
  RETURN_MEDIA_TYPE,
  RETURN_ITEM_STATUS_TO_BUCKET,
  RETURN_REFUND_FILTER,
  RETURN_REFUND_TEXT,
  RETURN_RESOLUTION_TO_DECISION_ACTION,
  RETURN_REVIEW_RESOLUTION,
  RETURN_STATUS,
  RETURN_STATUS_BUCKET,
  RETURN_STATUS_PROGRESSION,
  RETURN_STATUS_TO_BUCKET,
  RETURN_SERVER_STEP_TO_TIMELINE_STEP,
  RETURN_TIMELINE_SERVER_STATE_TO_STATE,
  RETURN_TIMELINE_STATE,
  RETURN_TIMELINE_STEP,
  RETURN_WORKLIST_STATUS_TO_DETAIL_STATUS,
  ReturnRefundFilter,
  ReturnDraftPhoto,
  ReturnReason,
  ReturnRequestDetail,
  ReturnRequestItem,
  ReturnRequestRow,
  ReturnStatus,
  ReturnTimelineEntry,
  ReturnTimelineStep,
  ReturnWorklistStatus,
  ReviewDecision,
} from "ecommerce/constants/returnRefund"
import {
  GetReturnRefundReviewDetailResponse,
  ListReturnRefundWorklistMetadata,
  ReturnedItemInspection,
  ReturnRefundItemDecision,
  ReturnRefundMediaUpload,
  ReturnRefundReviewItem,
  ReturnRefundReviewTimelineStep,
  ReturnRefundWorklistItem,
  ReturnRefundWorklistRequestRow,
} from "ecommerce/queries"

/** Matches the mock rows' pre-formatted `requestedAt`, e.g. `08 Mar 11:00`. */
const REQUESTED_AT_FORMAT = "DD MMM HH:mm"

const MINUTES_PER_HOUR = 60

const padUnit = (value: number): string => String(value).padStart(2, "0")

/*
 * One divmod core for both countdown formats, so they can only differ in the
 * separator — the compact form is not derived by post-processing the spaced one.
 */
const formatCountdown = (minutesLeft: number, separator: string): string => {
  const safeMinutes = Math.max(0, Math.floor(minutesLeft))
  const hours = Math.floor(safeMinutes / MINUTES_PER_HOUR)
  const minutes = safeMinutes % MINUTES_PER_HOUR

  return `${padUnit(hours)}${separator}${padUnit(minutes)}`
}

/**
 * Formats remaining SLA minutes as the design's `HH : MM` countdown, e.g. 45 → `00 : 45`,
 * 235 → `03 : 55`. Negative input clamps to `00 : 00` rather than rendering a negative
 * countdown — expiry behaviour itself is still undecided (Q24).
 */
export const formatSlaCountdown = (minutesLeft: number): string =>
  formatCountdown(minutesLeft, " : ")

/**
 * Abbreviated countdown for the drawer footer, e.g. `00:59`. The list headers use the
 * spaced `formatSlaCountdown` form instead.
 */
export const formatSlaCountdownCompact = (minutesLeft: number): string =>
  formatCountdown(minutesLeft, ":")

/**
 * True when the request still needs a merchant decision — judged from either level.
 *
 * An OR of the request status and the item statuses, because neither alone is reliable:
 * on a mixed-state request the header reads the least-progressed item (which can be a
 * closed status while another item is live), and a payload may stamp statuses on only
 * some items — or none. A request whose header is action-required while every item is
 * resolved cannot occur, since the header follows the least-progressed item.
 */
export const hasActionableItems = (detail: ReturnRequestDetail): boolean =>
  RETURN_STATUS_TO_BUCKET[detail.status] ===
    RETURN_STATUS_BUCKET.actionRequired ||
  detail.items.some(
    item =>
      item.status !== undefined &&
      RETURN_ITEM_STATUS_TO_BUCKET[item.status] ===
        RETURN_STATUS_BUCKET.actionRequired,
  )

/** True for a closed status — refunded, rejected or cancelled. */
export const isClosedStatus = (status: ReturnStatus): boolean =>
  RETURN_STATUS_TO_BUCKET[status] === RETURN_STATUS_BUCKET.closed

/** True when the request itself is closed. */
export const isClosedRequest = (detail: ReturnRequestDetail): boolean =>
  isClosedStatus(detail.status)

/**
 * An item's decision is `Completed` once it has an action — and, for a rejection,
 * a reason. Supporting photos stay optional (no asterisk in the design).
 */
export const isDecisionComplete = (decision?: ReviewDecision): boolean => {
  if (!decision) {
    return false
  }

  return (
    decision.action !== DECISION_ACTION.rejectRequest ||
    decision.reason !== undefined
  )
}

/**
 * The row's server-computed bucket. Returning `undefined` when absent is deliberate —
 * see `isOngoing` for why an unknown bucket must not be swept into a chip.
 */
const getRowBucket = (row: ReturnRequestRow): string | undefined => row.bucket

/** True when a server-supplied bucket is the merchant-action one. */
export const isActionRequiredBucket = (bucket: string | undefined): boolean =>
  bucket === RETURN_STATUS_BUCKET.actionRequired

/** True when the request sits in the Action Required bucket. */
export const isActionRequired = (row: ReturnRequestRow): boolean =>
  getRowBucket(row) === RETURN_STATUS_BUCKET.actionRequired

/**
 * True when the request matches the `Ongoing` chip — in progress without needing the
 * merchant to act.
 *
 * Two spellings are accepted for the server's own `bucket`: `ONGOING` (the value the
 * chip's own `metadata.bucketCounts` lookup uses) and `ACTION_NOT_REQUIRED`.
 *
 * Deliberately an equality check against a known set, not `bucket !== closed`: the
 * worklist returns terminal requests (`REFUNDED`, `CANCELLED`, `EXPIRED` are all offered
 * as status filters), so a negation would file every closed request under Ongoing. A
 * bucket matching neither spelling lands in `All` only, where it stays visible.
 */
export const isOngoing = (row: ReturnRequestRow): boolean => {
  const bucket = getRowBucket(row)

  return (
    bucket === RETURN_REFUND_FILTER.ongoing ||
    bucket === RETURN_STATUS_BUCKET.actionNotRequired
  )
}

export const filterReturnRequests = (
  rows: ReturnRequestRow[],
  filter: ReturnRefundFilter,
): ReturnRequestRow[] => {
  if (filter === RETURN_REFUND_FILTER.actionRequired) {
    return rows.filter(isActionRequired)
  }

  if (filter === RETURN_REFUND_FILTER.ongoing) {
    return rows.filter(isOngoing)
  }

  return rows
}

/**
 * Least-progressed status wins for the request header, so a request with one refunded item
 * and one still in transit reads as in transit rather than closed. Ranked via the
 * exhaustive `RETURN_STATUS_PROGRESSION` map so a new enum member breaks the build
 * instead of silently falling back to array order.
 */
const leastProgressedStatus = (statuses: ReturnStatus[]): ReturnStatus =>
  statuses.reduce((least, status) =>
    RETURN_STATUS_PROGRESSION[status] < RETURN_STATUS_PROGRESSION[least]
      ? status
      : least,
  )

/**
 * Applies submitted review decisions (decision 1) to a request, returning the updated
 * detail. Mixed outcomes are legitimate — one `Confirm` can approve one item's return and
 * refund another — so items are mapped individually and the header takes the
 * least-progressed result across ALL item statuses (the documented header invariant).
 *
 * The acknowledgement step completes and relabels; what follows depends on the outcome:
 * an open result advances to the next step, a fully closed one (every decision was
 * `Refund now`) terminates the timeline at `Refund approved` instead of promising a
 * return leg that will never happen. A timeline without a `pendingAcknowledgement`
 * step (already acknowledged, or a BE shape this mock never produces) is left as-is
 * rather than reshaped around a missing index.
 *
 * Exists because the drawer is mock-backed; once the mutation is real this becomes the
 * optimistic-update shape, and the server response replaces it. BP-54659 adds the
 * inspection variant.
 */
export const applyReviewDecisions = (
  detail: ReturnRequestDetail,
  decisions: Record<string, ReviewDecision>,
): ReturnRequestDetail => {
  const items = detail.items.map(item => {
    const decision = decisions[item.id]

    /*
     * A decision only lands on an item still awaiting decision 1. This matters because
     * the hook re-applies stored decisions onto every FRESH base the query delivers:
     * once the server has advanced an item past acknowledgement, stamping the old
     * decision status again would drag it backwards (e.g. an approved return already
     * in transit reverting to `returnRequested`).
     */
    const isAwaitingDecision =
      item.status === undefined ||
      item.status === RETURN_STATUS.acknowledgingRequest

    return decision && isAwaitingDecision
      ? { ...item, status: DECISION_ACTION_TO_STATUS[decision.action] }
      : item
  })

  /*
   * A FLAGGED item without its own status inherits the request's — dropping it would
   * let a partial decision set advance the header (and clear the SLA) past a line
   * that was never decided. Unflagged lines are context only: without an explicit
   * status of their own they must not hold the request back, or a fully decided
   * request could never reach its outcome (the review screen has nothing left to
   * offer for them).
   */
  const itemStatuses = items
    .map(item =>
      item.reason !== undefined ? item.status ?? detail.status : item.status,
    )
    .filter((status): status is ReturnStatus => status !== undefined)

  // Mixed outcomes are legitimate, so the header takes the least-progressed result.
  const status = itemStatuses.length
    ? leastProgressedStatus(itemStatuses)
    : detail.status

  const result: ReturnRequestDetail = {
    ...detail,
    items,
    status,
    /*
     * The server's status copy describes the status it arrived with. Once this
     * function moves the request to a different status, keeping it would pin the
     * header to stale copy (`statusLabel` wins over the FE label map), so it only
     * survives an unchanged status.
     */
    statusLabel: status === detail.status ? detail.statusLabel : undefined,
  }

  const acknowledgedIndex = detail.timeline.findIndex(
    entry => entry.step === RETURN_TIMELINE_STEP.pendingAcknowledgement,
  )

  /*
   * The acknowledgement step only completes when the request has actually left
   * acknowledgement — a partial decision set that leaves another flagged item
   * pending must not produce a timeline that contradicts the header.
   */
  if (
    acknowledgedIndex !== -1 &&
    status !== RETURN_STATUS.acknowledgingRequest
  ) {
    const acknowledged = {
      ...detail.timeline[acknowledgedIndex],
      step: RETURN_TIMELINE_STEP.requestAcknowledged,
      state: RETURN_TIMELINE_STATE.complete,
      detail: undefined,
      /*
       * The server title said "Pending acknowledgement" and would win over the FE
       * label for the rewritten step — the step changed, so its copy must too.
       */
      title: undefined,
    }

    const head = detail.timeline.slice(0, acknowledgedIndex)
    const tail = detail.timeline.slice(acknowledgedIndex + 1)

    if (status === RETURN_STATUS.refunded) {
      // Everything ended in a refund — no return leg, the timeline ends there.
      result.timeline = [
        ...head,
        acknowledged,
        {
          step: RETURN_TIMELINE_STEP.refundApproved,
          state: RETURN_TIMELINE_STATE.complete,
        },
      ]
    } else if (status === RETURN_STATUS.rejected) {
      /*
       * The rejection terminates the request. The tally is the request's total
       * rejected lines — counted from the same statuses the header was derived
       * from, so the two can never disagree — which distinguishes a full
       * rejection from a mixed outcome where other lines were refunded.
       */
      result.timeline = [
        ...head,
        acknowledged,
        {
          step: RETURN_TIMELINE_STEP.requestRejected,
          state: RETURN_TIMELINE_STATE.complete,
          count: itemStatuses.filter(
            itemStatus => itemStatus === RETURN_STATUS.rejected,
          ).length,
        },
      ]
    } else if (!isClosedStatus(status)) {
      // Still open — the next step becomes current.
      result.timeline = [
        ...head,
        acknowledged,
        ...tail.map((entry, index) =>
          index === 0
            ? { ...entry, state: RETURN_TIMELINE_STATE.current }
            : entry,
        ),
      ]
    } else {
      /*
       * Closed by cancellation — reachable when a pre-existing cancelled line is the
       * least-progressed status while this submission decided another. Cancellation
       * is not a merchant outcome, so the acknowledgement completes and the tail is
       * left as-is; a cancelled terminal step belongs to whoever owns that flow.
       */
      result.timeline = [...head, acknowledged, ...tail]
    }
  }

  /*
   * The review deadline only stops applying when nothing is left to act on — an
   * item outside this decision set can still be pending, and it keeps its countdown.
   */
  result.slaMinutesLeft = hasActionableItems(result)
    ? detail.slaMinutesLeft
    : undefined

  return result
}

/**
 * Success copy depends on the outcome: a pure `Refund now` submission gets the refund
 * wording, anything else gets the neutral "reviewed" line.
 */
export const getReviewSuccessMessage = (
  decisions: Record<string, ReviewDecision>,
): string => {
  const actions = Object.values(decisions).map(decision => decision.action)
  const isRefundOnly =
    actions.length > 0 &&
    actions.every(action => action === DECISION_ACTION.refundNow)

  return isRefundOnly
    ? RETURN_REFUND_TEXT.refundSuccess
    : RETURN_REFUND_TEXT.reviewSuccess
}

const MS_PER_MINUTE = 60_000

/**
 * Minutes left on the row's SLA, or `undefined` when it has no deadline.
 *
 * Worklist rows carry `nextActionDueAt` and the value is derived from `Date.now()` on
 * every render, so the countdown ticks (see `useMinuteTick`).
 *
 * NOTE: `nextActionDueAt`'s unit is not documented in the contract and no sample response
 * has yet carried the field. It is read as epoch milliseconds here, matching `submittedAt`
 * on the same message. Confirm with BE — if it is seconds, this reads ~56,000 years short
 * and the countdown clamps to `00 : 00`.
 */
export const getSlaMinutesLeft = (
  row: ReturnRequestRow,
): number | undefined => {
  if (row.nextActionDueAt === undefined) {
    return undefined
  }

  /*
   * `floor`, not `round`: the countdown is rendered as whole minutes, and rounding up
   * advertises a minute that has not started — 45m 31s would read "46 min left". An SLA
   * display must never overstate the time remaining.
   */
  return Math.floor((row.nextActionDueAt - Date.now()) / MS_PER_MINUTE)
}

/**
 * Prefix for the positional key a row falls back to when the response omits `requestID`.
 * Distinctive so such a row is recognisable in a React key warning or the DOM.
 */
const MISSING_REQUEST_ID_PREFIX = "missing-request-id-"

/** The earliest `submittedAt` across a request's items, in epoch milliseconds. */
const earliestSubmittedAt = (
  items: ReturnRefundWorklistItem[] | undefined,
): number | undefined =>
  (items ?? [])
    .map(item => item.submittedAt)
    .filter((submittedAt): submittedAt is number => submittedAt !== undefined)
    .sort((a, b) => a - b)[0]

/**
 * Maps one worklist row to the table's row shape.
 *
 * API rows carry no `status`: the FE `RETURN_STATUS` taxonomy never matched a backend
 * value, so the server's `requestStatus` (for filtering) and `statusContent` (for
 * display) are carried through instead.
 *
 * `submittedAt` is epoch **milliseconds**, so `dayjs()` rather than `dayjs.unix()` —
 * the latter expects seconds and renders a year in the tens of thousands.
 */
export const toReturnRequestRow = (
  row: ReturnRefundWorklistRequestRow,
  index: number = 0,
): ReturnRequestRow => {
  const submittedAt = earliestSubmittedAt(row.items)

  return {
    /*
     * One order can carry several requests (partial return, re-request after rejection),
     * so rows key on the request — never on `orderID`, which would collide and let Ant
     * Table conflate two rows, breaking selection and row clicks.
     *
     * `requestID` is expected on every row; the positional fallback exists only so a
     * malformed response degrades to "rows render, keys stay distinct" rather than
     * silently merging. It is deliberately not `orderID`-derived, since that is exactly
     * the value that collides.
     */
    requestID: row.requestID || `${MISSING_REQUEST_ID_PREFIX}${index}`,
    orderID: row.orderID ?? "",
    shortOrderNumber: row.shortOrderNumber ?? "",
    itemCount: row.items?.length ?? 0,
    /*
     * `!== undefined`, not truthiness: `0` is a valid epoch-millisecond value and would
     * otherwise be read as "no timestamp" and render blank.
     */
    requestedAt:
      submittedAt !== undefined
        ? dayjs(submittedAt).format(REQUESTED_AT_FORMAT)
        : "",
    requestStatus: row.requestStatus,
    // `badge.text` is the server's own rendering of the same status, so it stands in
    // when `requestStatusContent` is absent.
    statusContent: row.requestStatusContent ?? row.badge?.text,
    bucket: row.bucket,
    nextActionDueAt: row.nextActionDueAt,
  }
}

/**
 * Server-computed count for one bucket, from the worklist's `metadata.bucketCounts`.
 *
 * These counts are global to the merchant scope: they ignore `statusFilter` and the
 * current page, so they are the right source for the chips (which advertise the whole
 * queue) and the wrong source for "how many rows does this table hold".
 */
export const getBucketRequestCount = (
  metadata: ListReturnRefundWorklistMetadata | undefined,
  bucket: ReturnRefundFilter,
): number =>
  metadata?.bucketCounts?.find(bucketCount => bucketCount.bucket === bucket)
    ?.requestCount ?? 0

/** Matches the mock details' pre-formatted `requestedAt`, e.g. `13 Sep, Tue, 12:17 PM`. */
const DETAIL_REQUESTED_AT_FORMAT = "DD MMM, ddd, hh:mm A"

/** Timeline step timestamps, e.g. `13 Sep, 12:17 PM`. */
const TIMELINE_DETAIL_FORMAT = "DD MMM, hh:mm A"

const MS_PER_SECOND = 1000

/**
 * Backend item status → the FE detail taxonomy, mirroring the server's own
 * normalisation: an absent status reads as pending merchant review (the server's
 * `normalizeMexReviewItemStatus` does exactly this before building its response).
 *
 * An unknown non-empty status falls back to `returnRequested` — in-progress and NOT
 * merchant-actionable — because the two failure directions are not symmetric: wrongly
 * offering a Review CTA invites a decision the server will reject, while wrongly
 * parking a request under "in progress" is recoverable the moment the enum map learns
 * the new member.
 */
const toDetailStatus = (statusCode?: string): ReturnStatus => {
  if (!statusCode) {
    return RETURN_STATUS.acknowledgingRequest
  }

  return (
    RETURN_WORKLIST_STATUS_TO_DETAIL_STATUS[
      statusCode as ReturnWorklistStatus
    ] ??
    LEGACY_WORKLIST_STATUS_TO_DETAIL_STATUS[statusCode] ??
    RETURN_STATUS.returnRequested
  )
}

/**
 * `ITEM_DAMAGED` → `Item damaged`. A stopgap for enum-coded fields whose display copy
 * the contract defines but the server does not populate yet (`reasonDisplayName`) —
 * preferable to rendering the raw code, and dropped automatically once the copy lands
 * because the server value wins.
 */
const formatEnumLabel = (code?: string): string | undefined => {
  if (!code) {
    return undefined
  }

  const words = code
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .join(" ")

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : undefined
}

/**
 * Prefix for the positional id an item falls back to when the response omits `itemID`.
 * Same convention as `toReturnRequestRow`'s row fallback: two id-less items must never
 * share an id, or one review decision would silently stamp both.
 */
const MISSING_ITEM_ID_PREFIX = "missing-item-id-"

const toReturnRequestItem = (
  item: ReturnRefundReviewItem,
  index: number,
  /** The response's request-level room — see the `roomID` mapping below. */
  requestRoomID?: string,
): ReturnRequestItem => {
  const evidencePhotos = (item.media?.photos ?? [])
    .map(photo => photo.url)
    .filter((url): url is string => Boolean(url))

  /*
   * Both halves of the identity are required: the upload forms render these tiles
   * and key removal/replacement on the assetID, so an asset the server returns
   * without one could never be addressed again.
   */
  const draftPhotos = (item.draftMedia?.photos ?? []).flatMap(photo =>
    photo.assetID && photo.url
      ? [
          {
            assetID: photo.assetID,
            url: photo.url,
            fileName: photo.fileName || undefined,
          },
        ]
      : [],
  )

  const itemID = item.itemID || `${MISSING_ITEM_ID_PREFIX}${index}`

  return {
    /*
     * Item identity on the wire is the PAIR (itemID, displayModifierGroup) — the same
     * item can be claimed once per modifier variant — so the row id composites them.
     * Decisions keyed on this id must be split back into the pair when the submit
     * mutation lands.
     */
    id: item.displayModifierGroup
      ? `${itemID}::${item.displayModifierGroup}`
      : itemID,
    // The raw pair rides along so the decision submit never parses the composite.
    itemID: item.itemID || undefined,
    displayModifierGroup: item.displayModifierGroup || undefined,
    name: item.itemName ?? "",
    variant: item.displayModifierGroup || undefined,
    /*
     * Only an explicit status maps; an absent one stays absent so the item keeps the
     * drawer's inherit-from-request behaviour instead of being pinned to a default.
     */
    status: item.status?.statusCode
      ? toDetailStatus(item.status.statusCode)
      : undefined,
    /*
     * The cast marks the taxonomy seam: `reason` doubles as the "buyer flagged this
     * line" marker downstream (every claimed item carries one), while its display
     * runs through `reasonLabel`, so the FE reason enum never has to match the
     * backend's codes.
     */
    reason: (item.reason?.reasonCode as ReturnReason) || undefined,
    reasonLabel:
      item.reasonDisplayName || formatEnumLabel(item.reason?.reasonCode),
    quantity: item.quantity ?? 0,
    /*
     * Distinguishes the server being silent from the server saying "none": an absent
     * wire field stays `undefined` (FE offers the full action set while BE leaves the
     * field unpopulated), while a present list maps through the known resolutions —
     * so a list carrying only REQUEST_INFO becomes `[]` and blocks review.
     */
    eligibleActions: item.eligibleResolutions
      ? item.eligibleResolutions
          .map(resolution => RETURN_RESOLUTION_TO_DECISION_ACTION[resolution])
          .filter((action): action is DecisionAction => action !== undefined)
      : undefined,
    price: item.skuValueDisplay ?? "",
    imageURL: item.thumbnailURL || undefined,
    infoRequestUsed: item.infoRequestUsed || undefined,
    /*
     * The wire carries ONE room per request, at the response's top level; which
     * items are attached to it is what `infoRequestUsed` marks (attachment
     * happens exactly when an item's REQUEST_INFO is submitted). Stamping the
     * request's room onto attached items here keeps the derived FE field every
     * clarify surface reads — an unattached item must never inherit it.
     */
    roomID: item.infoRequestUsed ? requestRoomID || undefined : undefined,
    /*
     * REQUEST_INFO is filtered out of `eligibleActions` (it is not a radio option),
     * so its eligibility survives here — same silent-vs-none rule as above: an
     * absent list keeps the FE's permissive fallback.
     */
    canRequestInfo: item.eligibleResolutions
      ? item.eligibleResolutions.includes(RETURN_REVIEW_RESOLUTION.requestInfo)
      : undefined,
    buyerMessage: item.issueDescription || undefined,
    evidencePhotos: evidencePhotos.length ? evidencePhotos : undefined,
    draftPhotos: draftPhotos.length ? draftPhotos : undefined,
  }
}

/**
 * The clarify affordance's states, one rule for every surface that renders it —
 * the action sheet's row and the chat item picker must move together whenever
 * the server's eligibility contract changes.
 */
export const RETURN_CLARIFY_STATE = {
  /** Attached to the buyer conversation — offer `Go to chat`, never a submit. */
  chat: "CHAT",
  /** REQUEST_INFO still offered by the server — the live clarify entry. */
  available: "AVAILABLE",
  /** The once-per-item request is used and no room to return to — inert. */
  spent: "SPENT",
  /** Never offered for this item — no affordance at all. */
  hidden: "HIDDEN",
} as const

export type ReturnClarifyState = typeof RETURN_CLARIFY_STATE[keyof typeof RETURN_CLARIFY_STATE]

/*
 * The room wins over the flag (a conversation to return to beats a dead grey
 * label), and an absent `eligibleResolutions` keeps the permissive fallback,
 * like the decision radios.
 */
export const getClarifyState = (
  item: ReturnRequestItem,
): ReturnClarifyState => {
  if (item.roomID) {
    return RETURN_CLARIFY_STATE.chat
  }

  if (item.infoRequestUsed) {
    return RETURN_CLARIFY_STATE.spent
  }

  return item.canRequestInfo !== false
    ? RETURN_CLARIFY_STATE.available
    : RETURN_CLARIFY_STATE.hidden
}

/**
 * True only for an absolute https destination. The scheme check is the boundary that
 * matters — it keeps `window.open` from executing javascript:/data:/custom-scheme
 * values — while the ORIGIN is deliberately not allowlisted: the destination comes
 * from our own authenticated API (like `appealLink` and export URLs elsewhere in this
 * repo, all opened unvalidated), its hosts are BE-owned and environment-dependent, and
 * pinning them FE-side would silently no-op tracking links whenever infra changes.
 * BE's `grab://` deeplinks fail this check by design — they are unwrapped first by
 * `toTrackingHref`, and only the https page inside them is ever opened or forwarded.
 */
export const isSafeExternalUrl = (href: string): boolean => {
  try {
    return new URL(href).protocol === "https:"
  } catch {
    return false
  }
}

/**
 * The consumer app's routing key for the web-tracking screen — the discriminator that
 * says the deeplink's `page` IS the tracking destination. Other consumer deeplinks
 * reuse the `page` key for unrelated screens, so unwrapping without checking it would
 * route any of them through the tracking hand-off.
 */
const TRACKING_DEEPLINK_SCREEN_TYPE = "EXPRESS_WEB_TRACKING"

/**
 * The tracking page pulled OUT of the deeplink BE sends.
 *
 * The wire value is `grab://open?screenType=EXPRESS_WEB_TRACKING&page=<percent-encoded
 * https URL>`; `page` holds the Express tracking page itself. That inner URL is what
 * both hosts ultimately want — the desktop opens it as a plain web page, the webview
 * re-wraps it for the merchant app's own scheme via `toMexTrackingDeeplink`.
 *
 * A plain https `value` passes through unchanged, so this stays correct if BE switches
 * to sending the URL directly. Anything else — unparseable, a different `screenType`,
 * or a `page` that is not https — yields undefined.
 */
export const toTrackingHref = (value?: string): string | undefined => {
  if (!value) {
    return undefined
  }

  if (isSafeExternalUrl(value)) {
    return value
  }

  try {
    // `URL` parses custom schemes fine; `searchParams` also percent-decodes `page`.
    const url = new URL(value)

    // Only the documented consumer wrapper (`grab://open`) is unwrapped — any
    // other scheme or host is not the tracking deeplink the contract names, even
    // when it happens to carry the expected query parameters.
    if (url.protocol !== "grab:" || url.host !== "open") {
      return undefined
    }

    if (url.searchParams.get("screenType") !== TRACKING_DEEPLINK_SCREEN_TYPE) {
      return undefined
    }

    const page = url.searchParams.get("page")

    return page && isSafeExternalUrl(page) ? page : undefined
  } catch {
    return undefined
  }
}

/**
 * The merchant app's own tracking scheme. `EXPRESS` is the destination; `url` carries
 * the page, percent-encoded. Unlike BE's `grab://open?screenType=…` wrapper there is
 * no screen-type key — the host is the route.
 */
const MEX_TRACKING_DEEPLINK = "grabmex://EXPRESS?url="

/**
 * Rewrite BE's deeplink into the one the merchant app answers to.
 *
 * BE sends the consumer app's wrapper,
 * `grab://open?screenType=EXPRESS_WEB_TRACKING&page=<percent-encoded https URL>`. The
 * merchant app routes the same destination as `grabmex://EXPRESS?url=<percent-encoded
 * https URL>`, so only the tracking page survives the rewrite: `screenType` is the
 * consumer app's routing key and has no counterpart here.
 *
 * A plain https `value` converts too, so this keeps working if BE switches to sending
 * the page directly. Anything `toTrackingHref` cannot resolve to an https page yields
 * undefined rather than a half-built deeplink the app would reject.
 */
export const toMexTrackingDeeplink = (value?: string): string | undefined => {
  const page = toTrackingHref(value)

  return page
    ? `${MEX_TRACKING_DEEPLINK}${encodeURIComponent(page)}`
    : undefined
}

/**
 * The web hosts' shared hand-off for a timeline action: open the validated
 * destination in a new tab, silently no-op otherwise (`Contact Grab support` carries
 * no destination until BE fills its TODO, and its real handler is the chat slice).
 * The webview page never calls this — the native handler owns navigation there.
 */
export const openTimelineDestination = (href?: string): void => {
  if (href && isSafeExternalUrl(href)) {
    window.open(href, "_blank", "noopener")
  }
}

/**
 * A step's `detail` is an epoch-millisecond decimal string when the step has a
 * timestamp; otherwise the server's `subtitle` guidance stands in — matching the view
 * model's own convention (timestamp on completed steps, guidance on the ones ahead).
 */
const toTimelineDetail = (
  step: ReturnRefundReviewTimelineStep,
): string | undefined => {
  const occurredAt = Number(step.detail)

  if (step.detail && Number.isFinite(occurredAt) && occurredAt > 0) {
    return dayjs(occurredAt).format(TIMELINE_DETAIL_FORMAT)
  }

  return step.subtitle || undefined
}

/**
 * The server sends an action as (label, type, destination). The FE's contextual-action
 * plumbing is enum-keyed, so the pair is classified by the one distinction the server
 * itself draws: a destination means a tracking hand-off (`Track here`), no destination
 * means the Grab-support hand-off it sends for a failed/cancelled pickup. The server's
 * own label still wins in the UI via `actionLabel`.
 */
const toTimelineAction = (
  step: ReturnRefundReviewTimelineStep,
): Pick<ReturnTimelineEntry, "action" | "actionLabel" | "actionHref"> => {
  if (!step.actionName) {
    return {}
  }

  return {
    action: step.value
      ? RETURN_CONTEXTUAL_ACTION.trackHere
      : RETURN_CONTEXTUAL_ACTION.contactGrabSupport,
    actionLabel: step.actionName,
    // The RAW wire destination (`actionType` is DEEPLINK for both actions BE-side,
    // so it adds nothing to the classification) — preserved verbatim, because
    // `ReturnStatusTimeline` resolves it per host via `toTrackingHref` /
    // `toMexTrackingDeeplink` before anything navigates.
    actionHref: step.value || undefined,
  }
}

const toTimelineEntry = (
  step: ReturnRefundReviewTimelineStep,
): ReturnTimelineEntry => ({
  /*
   * Known server step codes map onto the FE step enum because BEHAVIOUR keys on it —
   * `applyReviewDecisions` finds the acknowledgement step by FE code to advance the
   * timeline after a submitted review. Display is unaffected either way: the
   * server-supplied `title` wins before any label lookup happens. Codes with no FE
   * equivalent pass through raw; the cast is safe because every keyed lookup on
   * `step` falls back to rendering the raw value.
   */
  step:
    RETURN_SERVER_STEP_TO_TIMELINE_STEP[step.stepCode ?? ""] ??
    ((step.stepCode ?? "") as ReturnTimelineStep),
  title: step.title || undefined,
  state:
    RETURN_TIMELINE_SERVER_STATE_TO_STATE[step.stepState ?? ""] ??
    RETURN_TIMELINE_STATE.upcoming,
  detail: toTimelineDetail(step),
  ...toTimelineAction(step),
})

/**
 * Maps the review-detail response to the drawer's view model.
 *
 * `undefined` on a response without a `requestID` — the server 404s a missing request,
 * so this only guards a malformed success payload.
 *
 * Presentation fields the contract defines but the server may not populate yet
 * (`requestStatusContent`, `itemsSectionTitle`, `totalValueDisplay`, `reasonDisplayName`)
 * map to optional view-model overrides: the drawer renders its own fallback copy until
 * they arrive, then the server's copy wins with no further FE change.
 */
export const toReturnRequestDetail = (
  response: GetReturnRefundReviewDetailResponse | undefined,
): ReturnRequestDetail | undefined => {
  if (!response?.requestID) {
    return undefined
  }

  /*
   * Header = least-progressed item (the documented invariant), with each statusless
   * item normalised the way the server itself does — absent status reads as pending
   * merchant review, never skipped, so a payload that stamps no statuses still
   * produces an actionable header rather than an empty reduction.
   */
  const headerStatuses = (response.items ?? []).map(item =>
    toDetailStatus(item.status?.statusCode),
  )
  const status = headerStatuses.length
    ? leastProgressedStatus(headerStatuses)
    : RETURN_STATUS.acknowledgingRequest

  return {
    requestID: response.requestID,
    orderID: response.orderID ?? "",
    orderNumber: response.shortOrderNumber || response.bookingCode || undefined,
    status,
    statusLabel: response.requestStatusContent || undefined,
    requestedAt:
      response.submittedAt !== undefined
        ? // Epoch milliseconds, so `dayjs()` rather than `dayjs.unix()`.
          dayjs(response.submittedAt).format(DETAIL_REQUESTED_AT_FORMAT)
        : "",
    itemsHeading: response.itemsSectionTitle || undefined,
    items: (response.items ?? []).map((item, index) =>
      toReturnRequestItem(item, index, response.roomID),
    ),
    total: response.totalValueDisplay || undefined,
    timelineTitle: response.timeline?.title || undefined,
    timeline: (response.timeline?.steps ?? []).map(toTimelineEntry),
    slaMinutesLeft: getReviewDetailSlaMinutesLeft(
      response.timeline?.action?.dueAt,
    ),
  }
}

/**
 * Minutes left on the review-detail SLA, from the timeline action's `dueAt` — the one
 * place the contract uses epoch SECONDS. Exported because the value is a snapshot of
 * `Date.now()`: `useReturnRequestDetail` re-derives it on a minute tick so the drawer's
 * countdown actually counts down instead of freezing at the mapped value.
 *
 * Floor, matching `getSlaMinutesLeft`: an SLA display must never overstate the time
 * remaining.
 */
export const getReviewDetailSlaMinutesLeft = (
  dueAt: number | undefined,
): number | undefined =>
  dueAt !== undefined && dueAt > 0
    ? Math.floor((dueAt * MS_PER_SECOND - Date.now()) / MS_PER_MINUTE)
    : undefined

/**
 * An item's inspection decision is complete once it has an outcome — and, for a
 * reported issue, a reason (the server rejects a reason-less issue outright).
 */
export const isInspectionDecisionComplete = (
  decision?: InspectionDecision,
): boolean => {
  if (!decision) {
    return false
  }

  return (
    decision.outcome !== INSPECTION_OUTCOME.reportIssue ||
    decision.reason !== undefined
  )
}

/**
 * Builds the inspection-submit payload from the inspection screen's per-item
 * choices. Same shape rules as `toReviewDecisionPayload`: wire identity from the
 * item (never parsed out of the composite UI id), `issueReason` only on a reported
 * issue, `displayModifierGroup` echoed even when empty, stray keys dropped.
 */
export const toInspectionPayload = (
  items: ReturnRequestItem[],
  decisions: Record<string, InspectionDecision>,
): ReturnedItemInspection[] =>
  items
    .filter(item => decisions[item.id] !== undefined)
    .map(item => {
      const decision = decisions[item.id]

      return {
        itemID: item.itemID ?? item.id,
        outcome: decision.outcome,
        ...(decision.outcome === INSPECTION_OUTCOME.reportIssue &&
        decision.reason !== undefined
          ? { issueReason: decision.reason }
          : {}),
        displayModifierGroup: item.displayModifierGroup ?? "",
      }
    })

/** Everything after the `data:*;base64,` prefix of a data URL. */
const DATA_URL_BASE64_PREFIX = /^data:[^;]*;base64,/

/** `.png` at the end of a file name, captured without the dot. */
const FILE_NAME_EXTENSION = /\.([a-z0-9]+)$/i

/**
 * The BARE file extension the upload endpoint expects (`jpg`/`jpeg`/`png`) — NOT the
 * MIME type: the server rejects anything non-alphanumeric outright (`image/png` fails
 * on the slash) and then allowlists the three extensions. The contract's example
 * payload says `image/jpeg`, but the deployed handler and every asset it returns
 * (`fileType: "jpeg"`) disagree — the code wins.
 */
const toPhotoFileExtension = (file: {
  name: string
  type?: string
}): string => {
  const fromMime = file.type?.split("/")[1]
  const fromName = FILE_NAME_EXTENSION.exec(file.name)?.[1]

  return (fromMime || fromName || "").toLowerCase()
}

/**
 * Builds one media-upload payload from a picked file and its data-URL contents.
 *
 * The server wants RAW base64 in `file.data` — the data-URL prefix `FileReader`
 * produces has to go.
 *
 * `mediaSource` defaults to the device library, which is what a web file picker
 * always is. The webview's Add-photos sheet passes its own value instead, so a
 * photo the merchant took with the camera is reported as one.
 */
export const toReturnRefundMediaUpload = (
  file: { name: string; type?: string; size?: number },
  dataUrl: string,
  mediaSource: string = RETURN_MEDIA_SOURCE.deviceLibrary,
): ReturnRefundMediaUpload => {
  // Numbers, not the doc's decimal strings — the Go DTO's size fields are int64.
  const size = file.size ?? 0
  const extension = toPhotoFileExtension(file)

  return {
    mediaType: RETURN_MEDIA_TYPE.photo,
    file: {
      name: file.name,
      type: extension,
      size,
      data: dataUrl.replace(DATA_URL_BASE64_PREFIX, ""),
    },
    fileName: file.name,
    fileType: extension,
    fileSize: size,
    mediaSource,
  }
}

/**
 * Marks a tile that renders a SERVER-staged draft rather than a local upload, so
 * `onChange`/`onRemove` can tell the two apart without a lookup.
 */
export const DRAFT_PHOTO_UID_PREFIX = "draft-photo-"

/**
 * The photo list an upload form renders: server-staged drafts (minus the ones the
 * merchant removed), then this session's own tiles.
 *
 * When both sides hold the same asset, THIS SESSION'S TILE WINS. It is already
 * mounted and already showing the picked file, so keeping it means the photo is
 * never swapped for an equivalent tile the moment the refetch lands — no remount,
 * no re-insertion animation, and no second fetch of the same image from the CDN.
 * The server copy takes over on the next open, when there is no local tile to
 * preserve.
 */
export const toRenderedPhotoList = (
  draftPhotos: ReturnDraftPhoto[] | undefined,
  localPhotos: UploadFile[] | undefined,
  removedAssetIDs: string[] | undefined,
): UploadFile[] => {
  const removed = new Set(removedAssetIDs ?? [])

  /*
   * Which assets this session's own tiles already show. `String(...)`, because
   * Duxton JSON-parses whatever `customRequest` hands `onSuccess` before
   * stamping it on the file — a numeric Galaxy id arrives back as a NUMBER.
   */
  const draftsByAssetID = new Map(
    (draftPhotos ?? []).map(photo => [photo.assetID, photo]),
  )

  const localAssetIDs = new Set<string>()
  ;(localPhotos ?? []).forEach(photo => {
    if (photo.response === undefined) {
      return
    }

    const assetID = String(photo.response)
    localAssetIDs.add(assetID)

    /*
     * Lend the tile the server's URL once its draft is known. The preview icon
     * is an anchor on `url || thumbUrl`, so without this it would open Duxton's
     * 200px thumbnail for the rest of the session instead of the full image.
     * Written ONTO the existing file object on purpose — Duxton caches
     * `thumbUrl` by mutating it, and handing back a copy would re-decode the
     * image on every render.
     */
    const draft = draftsByAssetID.get(assetID)
    if (draft && !photo.url) {
      // eslint-disable-next-line no-param-reassign
      photo.url = draft.url
    }
  })

  return [
    ...(draftPhotos ?? [])
      .filter(
        photo =>
          !removed.has(photo.assetID) && !localAssetIDs.has(photo.assetID),
      )
      .map(photo => ({
        uid: `${DRAFT_PHOTO_UID_PREFIX}${photo.assetID}`,
        name: photo.fileName || photo.assetID,
        status: "done" as const,
        url: photo.url,
      })),
    ...(localPhotos ?? []),
  ]
}

/*
 * `accept` only filters a native picker's own list, so drag-and-drop still has to
 * be checked here, and `file.type` is empty for sources the browser cannot sniff —
 * hence the extension fallback.
 *
 * JPG/JPEG/PNG PHOTOS ONLY, though the design asks for photos or videos: the
 * media-upload endpoint validates the PHOTO type and allowlists exactly these bare
 * extensions, so admitting anything else only defers the rejection to Confirm.
 * Video is raised with BE as a contract gap.
 */
const SUPPORTED_PHOTO_MIME = /^image\/(jpe?g|png)$/i
const SUPPORTED_PHOTO_EXTENSION = /\.(jpe?g|png)$/i

/**
 * The gate every added photo passes, whichever picker produced it — the browser
 * input's `beforeUpload` and the webview's native hand-off share it so the two
 * paths cannot drift on what they accept.
 */
export const isSupportedPhoto = (file: UploadFile): boolean => {
  const isImage = file.type
    ? SUPPORTED_PHOTO_MIME.test(file.type)
    : SUPPORTED_PHOTO_EXTENSION.test(file.name)

  // A KNOWN size is required: defaulting an unknown one to 0 would wave an
  // arbitrarily large blob past the gate (the server still enforces its limit).
  return (
    isImage &&
    file.size !== undefined &&
    file.size <= RETURN_MEDIA_MAX_PHOTO_SIZE_BYTES
  )
}

/** What both decision shapes hold for the upload form's photo list. */
export interface ReturnPhotoDecision {
  supportingPhotos?: UploadFile[]
  removedDraftAssetIDs?: string[]
}

interface StageNativePhotoOptions<Decision extends ReturnPhotoDecision> {
  /** The picked photo, already in the Upload-list shape. */
  photo: UploadFile
  /** Which picker produced it, as the enum the media endpoint expects. */
  mediaSource: string
  /**
   * The decision to start from when the item has none yet — the ONLY thing that
   * differs between the review card and the inspection card, which is why this is
   * a parameter rather than two copies of everything below.
   */
  defaultDecision: Decision
  /** Writes the decision through an updater, exactly as the card's `onChange` does. */
  onChange(update: (previous?: Decision) => Decision): void
  /** Stages the file as a server-side draft, resolving with its assetID. */
  upload(file: File, mediaSource: string): Promise<string>
}

/**
 * Walks one natively-picked photo through the lifecycle the `Upload` input runs
 * for a browser-picked one.
 *
 * A native pick never passes through the input, so it reaches neither
 * `beforeUpload` nor `customRequest`: this applies the same gate, shows the same
 * optimistic uploading tile, stages the file, and stamps the assetID exactly where
 * `onSuccess` would have put it — or drops the tile and toasts, the way a failed
 * browser upload does.
 *
 * Every write is an UPDATER: the stage resolves a tick or more after the pick, by
 * which time a captured decision is stale.
 */
export const stageNativePhoto = async <Decision extends ReturnPhotoDecision>({
  photo,
  mediaSource,
  defaultDecision,
  onChange,
  upload,
}: StageNativePhotoOptions<Decision>): Promise<void> => {
  const patchPhotos = (update: (photos: UploadFile[]) => UploadFile[]) =>
    onChange(previous => ({
      ...(previous ?? defaultDecision),
      supportingPhotos: update(previous?.supportingPhotos ?? []),
    }))

  const file = photo.originFileObj as File | undefined

  if (!file || !isSupportedPhoto(photo)) {
    notification.error({
      message: RETURN_REFUND_TEXT.reviewPhotoRejected(photo.name),
    })

    return
  }

  patchPhotos(current => [
    ...current,
    { ...photo, status: "uploading" as const },
  ])

  try {
    const assetID = await upload(file, mediaSource)

    patchPhotos(current =>
      current.map(entry =>
        entry.uid === photo.uid
          ? { ...entry, status: "done" as const, response: assetID }
          : entry,
      ),
    )
  } catch {
    // Same contract as the browser path: the tile goes and the toast asks for a
    // re-add, because nothing was staged for it.
    notification.error({
      message: RETURN_REFUND_TEXT.photoUploadFailed(file.name),
    })
    patchPhotos(current => current.filter(entry => entry.uid !== photo.uid))
  }
}

/**
 * Builds the decision-submit payload from the review screen's per-item choices.
 *
 * Walks the DETAIL's items rather than the decisions map so the wire identity comes
 * from the payload the decisions were made against — the composite UI id is never
 * parsed back apart, and a stray decision key that matches no item is dropped rather
 * than sent with a fabricated identity.
 *
 * `rejectionNote` is never sent: the reject form collects a reason and optional
 * photos, no free-text note. The photos themselves await the media-upload endpoint
 * (staged-draft flow) and are not part of this payload.
 */
export const toReviewDecisionPayload = (
  items: ReturnRequestItem[],
  decisions: Record<string, ReviewDecision>,
): ReturnRefundItemDecision[] =>
  items
    .filter(item => decisions[item.id] !== undefined)
    .map(item => {
      const decision = decisions[item.id]

      return {
        // The raw wire id when the item came from the API; the UI id as a fallback
        // keeps a malformed payload's positional id visible in the BE error.
        itemID: item.itemID ?? item.id,
        decision: DECISION_ACTION_TO_RESOLUTION[decision.action],
        ...(decision.action === DECISION_ACTION.rejectRequest &&
        decision.reason !== undefined
          ? { rejectionReason: decision.reason }
          : {}),
        // Echoed even when empty — identity server-side is the (itemID,
        // displayModifierGroup) pair.
        displayModifierGroup: item.displayModifierGroup ?? "",
      }
    })
